/**
 * Shared helpers for the patient detail screen (PatientPage + GameParameters).
 */

// Game list + labels are shared with the rest of the portal.
export { GAMES, gameLabel } from '../../games';

const toDate = (v) => {
    if (v == null || v === 0 || v === '') return null;
    const d = new Date(typeof v === 'string' && /^\d+$/.test(v) ? Number(v) : v);
    return Number.isNaN(d.getTime()) ? null : d;
};

/** "7 Oct 2026" */
export function fmtDay(v) {
    const d = toDate(v);
    return d ? d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
}

/** "7 Oct, 14:02" (year added when asked for, or when not this year). */
export function fmtDateTime(v, withYear = false) {
    const d = toDate(v);
    if (!d) return '—';
    const year = withYear || d.getFullYear() !== new Date().getFullYear();
    const day = d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', ...(year ? { year: 'numeric' } : {}) });
    const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    return `${day}, ${time}`;
}

/** Milliseconds → "4:12" or "1:02:03". */
export function fmtDuration(ms) {
    if (!Number.isFinite(ms) || ms <= 0) return '—';
    const total = Math.round(ms / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return h ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** "today" · "yesterday" · "3 days ago" · "7 Oct 2026" */
export function relativeDay(v) {
    const d = toDate(v);
    if (!d) return '';
    const start = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const days = Math.round((start(new Date()) - start(d)) / 86400000);
    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 7) return `${days} days ago`;
    return fmtDay(d);
}

/** Session length in ms: results.length when the server computed it, else end − start. */
export function sessionLength(s) {
    const len = Number(s?.results?.length);
    if (Number.isFinite(len) && len > 0) return len;
    const a = Number(s?.startTime);
    const b = Number(s?.endTime);
    return Number.isFinite(a) && Number.isFinite(b) && b > a ? b - a : null;
}

/** Error text from a failed response — production sends {error}, some routes {message}. */
export async function readError(res) {
    let msg = '';
    try {
        const text = await res.text();
        try {
            const j = JSON.parse(text);
            msg = (j && (j.error || j.message)) || '';
        } catch {
            msg = text && text.length < 200 && !/</.test(text) ? text.trim() : '';
        }
    } catch {
        /* body unreadable */
    }
    return msg || `The server returned an error (${res.status}).`;
}

/** JSON body, or null for 204 / an empty body (never calls res.json() on 204). */
export async function readJson(res) {
    if (res.status === 204) return null;
    const text = await res.text();
    if (!text || !text.trim()) return null;
    return JSON.parse(text);
}

/** Message for a thrown fetch error (network failures surface as TypeError). */
export const failMessage = (e, fallback = 'The server didn’t respond.') =>
    (e instanceof TypeError ? fallback : e?.message) || fallback;

/** Ensure a message ends like a sentence before more text is appended. */
export const sentence = (t) => {
    const x = String(t || '').trim();
    return !x || /[.!?…]$/.test(x) ? x : `${x}.`;
};
